import {
  MOCK_CAMPAIGN_SEND_STATS,
  MOCK_PRIOR_TOTALS,
} from '../data/mock-reports-data';
import { getSeededCampaignContent } from '../data/mock-link-heatmap-content';
import { toCampaignStats, toReportCampaignRow } from '../mappers/campaign-send-stats.mapper';
import {
  buildDeliverabilityPageModel,
  buildOverviewPageModel,
} from '../mappers/report-section-models.mapper';
import type { CampaignSendStats } from '../types/campaign-send-stats';
import type {
  DeliverabilityPageModel,
  OverviewPageModel,
} from '../types/report-section-models';
import type {
  CampaignReportMeta,
  CampaignStats,
  DeliverabilityReport,
  DeltaResult,
  ExportFormat,
  ExportJob,
  LinkReport,
  OverviewReport,
  RecipientEvent,
  ReportAlert,
  ReportCampaignRow,
  ReportRateKey,
} from '../types/reports.types';
import { rate, THRESHOLDS } from '../utils/report-rates';

const emptyAgg = (): CampaignStats & { campaigns: number } => ({
  audience: 0,
  sent: 0,
  delivered: 0,
  bounced: 0,
  bouncedHard: 0,
  bouncedSoft: 0,
  uniqueOpens: 0,
  uniqueClicks: 0,
  complaints: 0,
  unsubscribes: 0,
  skipTotal: 0,
  computedAt: new Date().toISOString(),
  campaigns: 0,
});

function windowBounds(
  days: number,
  from?: string,
  to?: string
): { fromMs: number; toMs: number } {
  if (from && to) {
    return {
      fromMs: new Date(`${from}T00:00:00`).getTime(),
      toMs: new Date(`${to}T23:59:59.999`).getTime(),
    };
  }
  const toMs = Date.now();
  return { fromMs: toMs - days * 86400000, toMs };
}

/**
 * Mirrors the Blocks filter the FE will send once CampaignSendStatss is live:
 * SentAt in range, Status in reportable set, IsDeleted != true.
 */
export function filterCampaignSendStats(
  docs: CampaignSendStats[],
  days: number,
  from?: string,
  to?: string,
  campaignId?: string | null
): CampaignSendStats[] {
  const { fromMs, toMs } = windowBounds(days, from, to);
  return docs
    .filter((d) => {
      if (d.IsDeleted) return false;
      if (!['sent', 'sending', 'failed'].includes(d.Status)) return false;
      if (campaignId && d.CampaignId !== campaignId) return false;
      const t = new Date(d.SentAt).getTime();
      return t >= fromMs && t <= toMs;
    })
    .sort((a, b) => String(b.SentAt).localeCompare(String(a.SentAt)));
}

function filterCampaigns(days: number, from?: string, to?: string): ReportCampaignRow[] {
  return filterCampaignSendStats(MOCK_CAMPAIGN_SEND_STATS, days, from, to).map(toReportCampaignRow);
}

function sumCampaigns(camps: ReportCampaignRow[]): CampaignStats & { campaigns: number } {
  const agg = emptyAgg();
  camps.forEach((c) => {
    agg.campaigns += 1;
    (
      [
        'audience',
        'sent',
        'delivered',
        'bounced',
        'bouncedHard',
        'bouncedSoft',
        'uniqueOpens',
        'uniqueClicks',
        'complaints',
        'unsubscribes',
        'skipTotal',
      ] as const
    ).forEach((k) => {
      agg[k] += c.stats[k] || 0;
    });
    if (c.stats.computedAt > agg.computedAt) agg.computedAt = c.stats.computedAt;
  });
  return agg;
}

function priorTotals(days: number, from?: string, to?: string): CampaignStats {
  const { fromMs, toMs } = windowBounds(days, from, to);
  const span = Math.max(toMs - fromMs, days * 86400000);
  const priorFrom = new Date(fromMs - span).toISOString().slice(0, 10);
  const priorTo = new Date(fromMs - 1).toISOString().slice(0, 10);
  const priorRows = filterCampaignSendStats(
    MOCK_CAMPAIGN_SEND_STATS,
    days,
    priorFrom,
    priorTo
  ).map(toReportCampaignRow);
  if (priorRows.length === 0) return MOCK_PRIOR_TOTALS;
  return sumCampaigns(priorRows);
}

function buildAlerts(camps: ReportCampaignRow[], totals: CampaignStats): ReportAlert[] {
  const out: ReportAlert[] = [];

  (Object.keys(THRESHOLDS) as Array<keyof typeof THRESHOLDS>).forEach((key) => {
    const def = THRESHOLDS[key];
    const r = rate(key, totals);
    if (!r || r.value < def.limit) return;
    out.push({
      metric: key,
      severity: def.severity,
      label: def.label,
      why: def.why,
      value: r.value,
      threshold: def.limit,
      scope: 'All campaigns in range',
    });
  });

  camps.forEach((c) => {
    const r = rate('complaintRate', c.stats);
    if (!r || r.value < THRESHOLDS.complaintRate.limit) return;
    out.push({
      metric: 'complaintRate',
      severity: 'critical',
      label: `Spam complaints over 0.1% on "${c.name}"`,
      why: THRESHOLDS.complaintRate.why,
      value: r.value,
      threshold: THRESHOLDS.complaintRate.limit,
      scope: c.name,
      campaignId: c.campaignId,
    });
  });

  const order = { critical: 0, warning: 1 };
  return out.sort((a, b) => order[a.severity] - order[b.severity] || b.value - a.value);
}

function buildComparison(
  totals: CampaignStats,
  prior: CampaignStats
): OverviewReport['comparison'] {
  const deltas: OverviewReport['comparison']['deltas'] = {};
  (
    [
      'deliveryRate',
      'openRate',
      'clickRate',
      'complaintRate',
      'bounceRate',
      'unsubscribeRate',
    ] as ReportRateKey[]
  ).forEach((key) => {
    const a = rate(key, totals);
    const b = rate(key, prior);
    deltas[key] =
      a && b
        ? { value: a.value, prior: b.value, delta: a.value - b.value }
        : a
          ? { value: a.value, prior: null, delta: null }
          : null;
  });
  deltas.delivered = prior.delivered
    ? {
        value: totals.delivered,
        prior: prior.delivered,
        delta: (totals.delivered - prior.delivered) / prior.delivered,
      }
    : { value: totals.delivered, prior: null, delta: null };
  return { deltas };
}

export function getOverviewReport(
  days: number,
  window?: { from: string; to: string }
): OverviewReport {
  const campaigns = filterCampaigns(days, window?.from, window?.to);
  const totals = sumCampaigns(campaigns);
  return {
    window: { days },
    totals,
    campaigns,
    comparison: buildComparison(totals, priorTotals(days, window?.from, window?.to)),
    alerts: buildAlerts(campaigns, totals),
  };
}

export function getDeliverabilityReport(
  days: number,
  campaignId?: string | null,
  window?: { from: string; to: string }
): DeliverabilityReport {
  const docs = filterCampaignSendStats(
    MOCK_CAMPAIGN_SEND_STATS,
    days,
    window?.from,
    window?.to,
    campaignId
  );
  const campaigns = docs.map(toReportCampaignRow);
  const totals = sumCampaigns(campaigns);
  return {
    window: { days },
    scope: campaignId ? 'campaign' : 'org',
    campaignId: campaignId || null,
    campaigns,
    totals,
    deliveryRate: rate('deliveryRate', totals),
    bounceRate: rate('bounceRate', totals),
    complaintRate: rate('complaintRate', totals),
    hardBounces: campaigns.reduce((n, c) => n + (c.stats.bouncedHard || 0), 0),
    softBounces: campaigns.reduce((n, c) => n + (c.stats.bouncedSoft || 0), 0),
    thresholds: { bounceRate: 0.05, complaintRate: 0.001 },
  };
}

/** Compact per-section models for BE review (rates in percent units). */
export function getOverviewPageModel(
  days: number,
  window: { from: string; to: string }
): OverviewPageModel {
  return buildOverviewPageModel(
    getOverviewReport(days, window),
    getDeliverabilityReport(days, null, window),
    { days, from: window.from, to: window.to }
  );
}

export function getDeliverabilityPageModel(
  days: number,
  window: { from: string; to: string },
  campaignId?: string | null
): DeliverabilityPageModel {
  return buildDeliverabilityPageModel(getDeliverabilityReport(days, campaignId, window), {
    days,
    from: window.from,
    to: window.to,
  });
}

export function listReportableCampaigns(): CampaignReportMeta[] {
  return MOCK_CAMPAIGN_SEND_STATS.filter((d) => !d.IsDeleted).map((d) => ({
    campaignId: d.CampaignId,
    name: d.Name,
    status: d.Status,
    sentAt: d.SentAt,
    stats: toCampaignStats(d),
  }));
}

export function getCampaignReport(campaignId: string): CampaignReportMeta | null {
  if (!campaignId) return null;
  const row = MOCK_CAMPAIGN_SEND_STATS.find((c) => c.CampaignId === campaignId);
  if (row) {
    return {
      campaignId: row.CampaignId,
      name: row.Name,
      status: row.Status,
      sentAt: row.SentAt,
      stats: toCampaignStats(row),
    };
  }

  /* Real campaign IDs from the API are not in the prototype seed. Until
     CampaignSendStats is populated, synthesize a deterministic report so
     "View report" from the campaigns list still lands on a usable screen. */
  const hash = Array.from(campaignId).reduce((n, ch) => n + ch.charCodeAt(0), 0);
  const sent = 600 + (hash % 400);
  const delivered = Math.floor(sent * (0.96 + (hash % 3) * 0.01));
  const uniqueOpens = Math.floor(delivered * (0.22 + (hash % 5) * 0.02));
  const uniqueClicks = Math.floor(uniqueOpens * (0.2 + (hash % 4) * 0.05));
  const bounced = sent - delivered;
  return {
    campaignId,
    name: 'Campaign report',
    status: 'sent',
    sentAt: new Date(Date.now() - (hash % 14) * 86400000).toISOString(),
    stats: {
      audience: sent + 40,
      sent,
      delivered,
      bounced,
      bouncedHard: Math.floor(bounced * 0.55),
      bouncedSoft: bounced - Math.floor(bounced * 0.55),
      uniqueOpens,
      uniqueClicks,
      complaints: hash % 7 === 0 ? Math.max(1, Math.ceil(delivered * 0.0012)) : 0,
      unsubscribes: Math.floor(delivered * 0.002),
      skipTotal: 40,
      sendableCount: sent,
      skipReasons: { suppressed: 18, unsubscribed: 14, invalid: 8 },
      balances: true,
      computedAt: new Date().toISOString(),
    },
  };
}

export function getLinkReport(campaignId: string): LinkReport {
  const camp = getCampaignReport(campaignId);
  const seeded = getSeededCampaignContent(campaignId, camp?.name || 'Campaign');
  const rows = seeded.links.map((r) => ({
    linkId: r.linkId,
    url: r.url,
    label: r.label,
    position: r.position,
    isUnsubscribe: r.isUnsubscribe,
    clicks: r.clicks,
    uniqueClicks: r.uniqueClicks,
    share: 0,
  }));
  const totalClicks = rows.reduce((n, r) => n + r.clicks, 0);
  const totalUnique = rows.reduce((n, r) => n + r.uniqueClicks, 0);
  rows.forEach((r) => {
    r.share = totalClicks ? r.clicks / totalClicks : 0;
  });
  rows.sort((a, b) => b.clicks - a.clicks || (a.position ?? 0) - (b.position ?? 0));
  return {
    rows,
    unplaced: [],
    totalClicks,
    totalUnique,
    maxClicks: rows.reduce((m, r) => Math.max(m, r.clicks), 0),
    contentHtml: seeded.html,
  };
}

export function getRecipientEventCount(campaignId: string): number {
  const camp = getCampaignReport(campaignId);
  if (!camp) return 0;
  return Math.max(
    camp.stats.delivered + camp.stats.uniqueOpens + camp.stats.uniqueClicks + camp.stats.bounced,
    48
  );
}

export function getRecipientEvents(
  campaignId: string,
  q = '',
  type?: string
): RecipientEvent[] {
  const camp = getCampaignReport(campaignId);
  if (!camp) return [];
  const types: Array<{
    type: string;
    label: string;
    tone: RecipientEvent['tone'];
    url?: string;
    skipReason?: string;
  }> = [
    { type: 'delivered', label: 'Delivered', tone: 'success' },
    { type: 'open', label: 'Opened', tone: 'info' },
    {
      type: 'click',
      label: 'Clicked',
      tone: 'info',
      url: 'https://example.com/pricing',
    },
    { type: 'bounce-hard', label: 'Bounced (hard)', tone: 'error' },
    { type: 'bounce-soft', label: 'Bounced (soft)', tone: 'warning' },
    { type: 'complaint', label: 'Marked as spam', tone: 'error' },
    { type: 'skipped', label: 'Skipped', tone: 'neutral', skipReason: 'suppressed' },
    { type: 'unsubscribe', label: 'Unsubscribed', tone: 'warning' },
  ];
  const rows: RecipientEvent[] = [];
  for (let i = 0; i < 48; i++) {
    const t = types[i % types.length];
    if (type && type !== 'all' && t.type !== type) continue;
    const address = `contact${(i + 1) * 17}@example.com`;
    if (q.trim() && !address.toLowerCase().includes(q.trim().toLowerCase())) continue;
    const when = new Date(camp.stats.computedAt);
    when.setMinutes(when.getMinutes() - i * 17);
    rows.push({
      itemId: `${campaignId}-ev-${i}`,
      type: t.type,
      address,
      occurredAt: when.toISOString(),
      label: t.label,
      tone: t.tone,
      url: t.url,
      skipReason: t.skipReason,
      userAgentFamily: t.type === 'click' ? (i % 2 ? 'Mobile Safari' : 'Chrome') : undefined,
    });
  }
  return rows;
}

const exportJobs = new Map<string, ExportJob>();

export function requestExport(opts: {
  report: string;
  format: ExportFormat;
  days?: number;
  campaignId?: string;
}): ExportJob {
  const labels: Record<string, string> = {
    overview: 'Dashboard overview',
    deliverability: 'Deliverability',
    'campaign-report': 'Campaign report',
    'campaign-links': 'Link report',
  };
  const itemId = `exp-${Date.now()}`;
  const job: ExportJob = {
    itemId,
    report: opts.report,
    label: labels[opts.report] || 'Report',
    format: opts.format,
    status: 'queued',
    rowCount: null,
    sizeBytes: null,
    expiresAt: null,
  };
  exportJobs.set(itemId, job);
  return job;
}

export function advanceExport(itemId: string): ExportJob | null {
  const job = exportJobs.get(itemId);
  if (!job) return null;
  if (job.status === 'queued') {
    job.status = 'in_progress';
    return { ...job };
  }
  if (job.status === 'in_progress') {
    job.status = 'completed';
    job.rowCount = job.report === 'overview' ? MOCK_CAMPAIGN_SEND_STATS.length : 1;
    job.sizeBytes = Math.max(512, (job.rowCount || 1) * 86);
    job.expiresAt = new Date(Date.now() + 72 * 3600000).toISOString();
    return { ...job };
  }
  return { ...job };
}

export function getExportJob(itemId: string): ExportJob | null {
  const job = exportJobs.get(itemId);
  return job ? { ...job } : null;
}

export type { DeltaResult };
